import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
interface StatItem {
  label: string;
  value: number;
  suffix: string;
  description: string;
}
const stats: StatItem[] = [{
  label: "Efficiency Gain",
  value: 40.0,
  suffix: "%",
  description: "Streamlining digital workflows to eliminate operational bottlenecks and friction."
}, {
  label: "Cost Optimization",
  value: 25.5,
  suffix: "%",
  description: "Reducing infrastructure overhead through strategic technical oversight."
}, {
  label: "Process Velocity",
  value: 60.0,
  suffix: "%",
  description: "Accelerating internal infrastructures with modern technology integration."
}, {
  label: "Advisory Accuracy",
  value: 99.5,
  suffix: "%",
  description: "Precision-driven strategic consulting for organizational excellence and growth."
}];
function AnimatedStat({
  item,
  index
}: {
  item: StatItem;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const intervalTime = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progressRatio = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progressRatio, 3);
      const nextCount = item.value * easeOut;
      const nextProgress = item.value * easeOut;
      setCount(nextCount);
      setProgress(nextProgress);
      if (currentStep >= steps) {
        setCount(item.value);
        setProgress(item.value);
        clearInterval(timer);
      }
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isVisible, item.value]);
  return <div data-section-id="26934" ref={ref} className="h-full" data-index={index}>
      <Card className="h-full border-border/40 bg-card/5 backdrop-blur-sm transition-all duration-300 hover:bg-card/10 hover:border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-muted-foreground font-mono text-[12px] uppercase tracking-[0.15em]">
            {item.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-baseline gap-1">
            <span className="font-mono text-5xl font-bold tracking-tighter text-foreground md:text-6xl">
              {count.toFixed(1)}
            </span>
            <span className="font-mono text-2xl text-primary md:text-3xl">
              {item.suffix}
            </span>
          </div>
          
          <div className="space-y-2">
            <Progress value={progress} className="h-px bg-muted/20" />
            <p className="text-muted-foreground text-sm leading-relaxed font-sans">
              {item.description}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function QuantifiableImpact() {
  return <section id="strategic-impact" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 h-full w-full bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.05]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl mb-6 font-sans">
            Engineered for <span className="text-primary">Strategic Impact</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed font-sans">
            Century Pointe Holdings provides technical oversight and high-level consulting to help organizations 
            streamline digital workflows and internal infrastructures for peak performance.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => <AnimatedStat key={i} item={stat} index={i} />)}
        </div>
      </div>
    </section>;
}